/*
 * THE PENDING SCREEN (direction §12, contract §1).
 *
 * "Pending state: names the mechanism (invite-only, human review), sets an
 * expectation (turnaround copy must match reality), shows the signed-up email so
 * typos self-diagnose, offers sign-out. No apology; waiting isn't an error."
 *
 * §12's sign-out is NOT offered to a pending account any more -- see below.
 *
 * THREE JOBS, and each sentence below does one of them:
 *
 *   1. NAME THE MECHANISM. "In beta, invite-only" tells someone their account is
 *      not broken and no amount of retrying will help. A generic "your account is
 *      being set up" invites exactly that retrying.
 *   2. SET AN EXPECTATION THAT MATCHES REALITY. This is the constrained one, and
 *      what it is constrained BY changed with build item 5 (Kevin, 2026-09-15).
 *      Contract §1 asks whether approval sends an email, and since stage 4b
 *      phase D the answer is yes: approving an account mails it (decision 604).
 *      The copy says "we will let you know", and that sentence was written to
 *      survive this change — it was true before, when the invite list was
 *      hand-picked and Kevin told each person himself, and it is true now that
 *      the app sends the news. What changed is who sends it, not what this
 *      screen claims. deviations.md #2 records the history.
 *      Shown only once the address is settled; until then job 3 has the space.
 *   3. SHOW THE EMAIL -- UNTIL IT IS VERIFIED. So a typo diagnoses itself:
 *      someone who typed "gmial.com" can see it here, re-send the link, or FIX
 *      it here rather than only see it (decision 610): the person most likely
 *      to need the change form is the one waiting at the wrong address. Once
 *      the address is verified (and no change is in flight) the whole block
 *      goes, and the screen ends at the expectation (Kevin's review): a proven
 *      address has nothing left to diagnose, and the box would be noise.
 *
 * NO SIGN-OUT FOR A PENDING ACCOUNT (Kevin's review, decision 610). Someone
 * who has just signed up does not know they are signed in, so a "Sign out"
 * button reads as a puzzle rather than an exit. It was once the only way to
 * fix a typo (sign out, register again); the change form is that fix now.
 *
 * A DISABLED ACCOUNT gets the screen as it was: the address, no controls --
 * the server refuses it the address endpoints (610 admits pending, not
 * disabled), and controls that can only fail are worse than none -- and it
 * KEEPS sign-out, because it will never reach Settings to find another.
 *
 * NO APOLOGY, per §12. No "sorry", no error styling, no warning icon. A pending
 * account is the system working exactly as designed — the invite posture is
 * deliberate (direction §2) — and copy that apologised for it would be the app
 * describing its own intended behaviour as a fault.
 */

import { useTranslation } from "react-i18next";

import { Brand } from "../components/Brand";
import { AddressFlag, EmailAddress } from "../components/EmailAddress";
import type { User } from "../lib/api";
import styles from "./SignIn.module.css";

export function Pending({
  user,
  onSignOut,
  onUserChanged,
}: {
  user: User;
  onSignOut: () => void;
  onUserChanged: (user: User) => void;
}) {
  const { t } = useTranslation();
  const disabled = user.status === "disabled";
  const addressSettled = user.email_status === "verified" && !user.pending_email;

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        {/* Static, not animated: §8 plays the animation at sign-in and cold
            launch, and arriving here is neither — it is a state you were already
            in. */}
        <div className={styles.brand}>
          <Brand variant="lockup" height={34} />
        </div>

        <h1 className={styles.heading}>{t("pending.heading")}</h1>

        {/* Job 1: the mechanism. */}
        <p>{t("pending.mechanism")}</p>

        {/*
          Job 2: the expectation, worded to match what actually happens --
          ONCE THE ADDRESS IS SETTLED (Kevin's review). Before that the box
          below is the thing to act on, and a sentence about waiting would pull
          the eye away from it; after it, the box goes and this takes its
          place. A disabled account sees both, as it always did.
        */}
        {disabled || addressSettled ? <p>{t("pending.expectation")}</p> : null}

        {/*
          Job 3: the address. In a <dl> because it is a labelled value rather than
          a sentence, and the label is what makes the typo visible as a typo.
        */}
        {disabled || !addressSettled ? (
          <dl className={styles.emailBlock}>
            <dt className={styles.label}>{t("pending.signedUpAs")}</dt>
            <dd className={styles.email}>
              {user.email}
              <AddressFlag user={user} />
            </dd>
            {!disabled ? (
              <dd className={styles.emailControls}>
                <EmailAddress user={user} variant="pending" onUserChanged={onUserChanged} />
              </dd>
            ) : null}
          </dl>
        ) : null}

        {/* A disabled account's only action. Outline, not filled — it is the
            way out, not the thing you came to do. */}
        {disabled ? (
          <button className={styles.outline} type="button" onClick={onSignOut}>
            {t("settings.signOut")}
          </button>
        ) : null}
      </div>
    </div>
  );
}
